import { env } from "@platform/core/env";
import { PLATFORM_SESSION_CHANGED } from "@lawline/shared/access/platform";
import { pageSession } from "./page-session";

// Mirrors apps/api ErrorBody.
interface ApiErrorBody {
	error: {
		type: string;
		code: string;
		message: string;
		request_id: string;
		details?: unknown;
	};
}

export class ApiError extends Error {
	readonly status: number;
	readonly type: string;
	readonly code: string;
	readonly requestId?: string;
	readonly details?: unknown;

	constructor(status: number, body: Partial<ApiErrorBody["error"]>) {
		super(body.message ?? `Request failed (${status})`);
		this.name = "ApiError";
		this.status = status;
		this.type = body.type ?? "unknown";
		this.code = body.code ?? "unknown";
		this.requestId = body.request_id;
		this.details = body.details;
	}
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
	// FormData sets its own multipart content-type (with boundary); leave it be.
	const isForm = init?.body instanceof FormData;
	const res = await fetch(`${env.API_BASE_URL}${path}`, {
		credentials: "include",
		...init,
		headers: {
			...(isForm ? {} : { "content-type": "application/json" }),
			...init?.headers,
			...pageSession.headers(),
		},
	});

	if (!res.ok) {
		const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
		if (body?.error.code === PLATFORM_SESSION_CHANGED) pageSession.invalidate();
		throw new ApiError(res.status, body?.error ?? {});
	}
	if (res.status === 204) return undefined as T;
	return (await res.json()) as T;
}

export const api = {
	get: <T>(path: string) => request<T>(path),
	post: <T>(path: string, body?: unknown) =>
		request<T>(path, {
			method: "POST",
			body: body === undefined ? undefined : JSON.stringify(body),
		}),
	patch: <T>(path: string, body: unknown) =>
		request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
	upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
	del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
